import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const About = () => {
  const handleConsultation = () => {
    window.open('https://wa.me/918185882299?text=Hi! I would like to get a free consultation.', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            We Build <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Growth Systems</span>
            <br />
            Not Just Marketing
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            At OmniCore X Quantum, we focus on building complete digital systems that help businesses attract, convert, and grow consistently using modern marketing and AI-driven strategies.
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-20">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-cyan-500/30">
            <img
              src="https://images.pexels.com/photos/8386437/pexels-photo-8386437.jpeg"
              alt="AI-Powered Innovation"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
            <div className="absolute bottom-8 left-8 right-8">
              <h2 className="text-3xl font-bold text-white mb-2">AI-Powered Growth. Engineered for Market Dominance.</h2>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-10 rounded-3xl hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30">
            <h3 className="text-3xl font-bold text-white mb-6">
              Our <span className="text-cyan-400">Mission</span>
            </h3>
            <p className="text-gray-400 text-lg leading-relaxed mb-4">
              We transform businesses through cutting-edge digital marketing strategies powered by artificial intelligence and data-driven insights.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              Our mission is to empower hospitals and businesses with complete growth systems that generate consistent leads, enhance visibility, and drive sustainable revenue growth.
            </p>
          </Card>

          <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-10 rounded-3xl hover:border-purple-400 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/30">
            <h3 className="text-3xl font-bold text-white mb-6">
              Our <span className="text-purple-400">Approach</span>
            </h3>
            <p className="text-gray-400 text-lg leading-relaxed mb-4">
              We don't just run ads or manage social media. We build integrated systems that combine advertising, content, websites, and strategic planning.
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              Every strategy is customized to your unique business goals, target audience, and competitive landscape, ensuring maximum ROI and sustainable growth.
            </p>
          </Card>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            What Makes Us <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Different</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-8 rounded-2xl hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/50">
                <span className="text-3xl font-bold text-white">01</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">System-First Thinking</h3>
              <p className="text-gray-400 leading-relaxed">
                We build complete growth systems, not isolated campaigns. Every element works together to drive results.
              </p>
            </Card>

            <Card className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 p-8 rounded-2xl hover:border-purple-400 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/30 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-purple-500/50">
                <span className="text-3xl font-bold text-white">02</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">AI-Powered Optimization</h3>
              <p className="text-gray-400 leading-relaxed">
                Leverage cutting-edge AI technology to optimize campaigns, predict trends, and maximize performance.
              </p>
            </Card>

            <Card className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 p-8 rounded-2xl hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30 hover:scale-105">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/50">
                <span className="text-3xl font-bold text-white">03</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Results-Driven Focus</h3>
              <p className="text-gray-400 leading-relaxed">
                Every decision is made with your growth in mind. We measure success by leads generated and revenue increased.
              </p>
            </Card>
          </div>
        </div>

        {/* Who We Serve */}
        <div className="mb-20">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Who We <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">Serve</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-gradient-to-br from-cyan-900/20 to-black border border-cyan-500/30 p-8 rounded-2xl hover:border-cyan-400 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">Healthcare Providers</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Hospitals, clinics, and medical practices looking to attract more patients, build trust, and improve their online presence.
              </p>
              <ul className="space-y-2 text-gray-400">
                <li>• Patient acquisition systems</li>
                <li>• Reputation management</li>
                <li>• Local SEO and GBP optimization</li>
                <li>• Healthcare-compliant advertising</li>
              </ul>
            </Card>

            <Card className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-500/30 p-8 rounded-2xl hover:border-purple-400 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-4">Businesses & Enterprises</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Companies of all sizes seeking to scale their digital presence, generate quality leads, and dominate their market.
              </p>
              <ul className="space-y-2 text-gray-400">
                <li>• Lead generation systems</li>
                <li>• Brand positioning and strategy</li>
                <li>• Multi-channel advertising</li>
                <li>• Conversion optimization</li>
              </ul>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-cyan-900/40 to-purple-900/40 border border-cyan-500/50 p-12 rounded-3xl backdrop-blur-sm shadow-2xl shadow-cyan-500/30">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can build a customized growth system for your business
            </p>
            <Button
              onClick={handleConsultation}
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105"
            >
              Get Free Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;
